import { MediumLinkButton, MediumPrimaryButton } from "@worthy-npm/clarity";
import _ from "lodash";
import I18n from "lib/i18n/i18n";
import UserActions from "lib/userActions";
import PropTypes from "prop-types";
import React from "react";
import {
    DiamondClarity,
    DiamondColor,
    GemstoneColor,
} from "../../constants/itemDetails";
import { appUrl, isMobile } from "../../helpers/ApplicationHelper";
import WorthyApi from "../../lib/api/worthyApi";
import CheckboxGroup from "./CheckboxGroup";
import FreeText from "./FreeText";
import ChocolateTableChoice from "./ChocolateTableChoice";
import Buttons from "./Buttons";
import { MultiInputWrapper, Title } from "./styles";
import Text from "./Text";
import Weight from "./Weight";
import GA from "../../lib/ga";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { updateItem } from "actions/item";

class Survey extends React.Component {
    constructor(props) {
        super(props);
        this.isMobile = isMobile();

        let questions = I18n.getString(
            `survey::${props.surveyToShow}::questions`
        );

        if (props.surveyToShow === "giaCertificateOrProperties") {
            questions = [questions[0], questions[1], questions[3], questions[4], questions[5], questions[6]];
        }

        this.state = {
            step: 0,
            questions,
            answers: this.props.defaultAnswers || {},
            hideThankYouStep: props.showThankYouExp
                ? false
                : I18n.getString(
                      `survey::${props.surveyToShow}::hideThankYouStep`
                  ),
        };
    }

    componentDidMount() {
        const { ObjectId, ObjectType, surveyToShow } = this.props;
        UserActions.survey(
            "Survey modal presented",
            ObjectId,
            ObjectType,
            "",
            surveyToShow
        );
    }

    onChangeSingle = (key, answer) => {
        this.setState((state) => ({
            answers: { ...state.answers, [key]: answer },
        }));
    };

    onChangeMultiple = (key, answer, isChecked) => {
        const { answers } = this.state;
        const multipleAnswers = answers[key] || [];

        isChecked
            ? multipleAnswers.push(answer)
            : multipleAnswers.splice(multipleAnswers.indexOf(answer), 1);

        this.setState((state) => ({
            answers: { ...state.answers, [key]: multipleAnswers },
        }));
    };

    progressBarClicked(e) {
        let { step } = this.state;
        const { surveyToShow } = this.props;
        const requestedStep = parseInt(e.target.attributes.id.value);

        let questions = I18n.getString(`survey::${surveyToShow}::questions`);

        if (surveyToShow === "giaCertificateOrProperties") {
            questions = [questions[0], questions[1], questions[3], questions[4], questions[5], questions[6]];
        }

        if (step !== requestedStep) {
            this.setSurveyTitle(requestedStep);
            this.setState({
                step: requestedStep,
                questions,
            });
        }
    }

    saveEvent(event, value = "") {
        const { ObjectId, ObjectType, surveyToShow } = this.props;
        UserActions.survey(event, ObjectId, ObjectType, value, surveyToShow);
    }

    async _updateProperty(
        propertyName,
        possibleValues,
        singleAnswer,
        isGemstone = false
    ) {
        const { ObjectId, updateItem } = this.props;
        const lowCaseSingleAnswer = singleAnswer.toLowerCase();

        if (Object.values(possibleValues).includes(lowCaseSingleAnswer)) {
            try {
                await WorthyApi.updateProperty(
                    ObjectId,
                    propertyName,
                    lowCaseSingleAnswer,
                    isGemstone
                );
                updateItem(ObjectId);
            } catch (err) {
                console.error(
                    `Failed update item's property ${propertyName}: ${singleAnswer}`
                );
            }
        }
    }

    updateColorOrClarity(singleAnswer, question) {
        const { isGemstone } = this.props;
        const color = isGemstone ? GemstoneColor : DiamondColor;

        if (question.includes("color")) {
            this._updateProperty("color", color, singleAnswer, isGemstone);
            return;
        }

        if (question.includes("clarity")) {
            this._updateProperty(
                "clarity",
                DiamondClarity,
                singleAnswer,
                isGemstone
            );
        }
    }

    updateCaratWeight(rawCaratWeight) {
        const { ObjectId, updateItem } = this.props;
        const caratWeight = parseFloat(rawCaratWeight);
        if(isNaN(caratWeight) || caratWeight <= 0 || caratWeight > 50 || caratWeight.toString() !== rawCaratWeight) {
            window && window.Rollbar && window.Rollbar.error('Wrong carat weight on Survey', {rawCaratWeight, caratWeight});
            return;
        };

        WorthyApi.updateCaratWeight(ObjectId , caratWeight)
            .then(() => {
                updateItem(ObjectId);
            })
            .catch((err) => {
                window && window.Rollbar && window.Rollbar.error(err, {ObjectId, caratWeight});
            });
    }

    updateCertificatePresence = (value) => {
        const q = I18n.getString(`survey::${this.props.surveyToShow}::questions`);
        const questions = [q[0], q[2], q[3], q[4], q[5], q[6]];

        this.props.setTitleDesktop('');
        this.saveEvent(q[1].q, [q[1].buttons[Number(value)].text]);

        if (value === false) {
            this.setState({ questions });
            return;
        }

        this.setState({ step: 2 });
    };


    handledCertificateStep = (stopAnimation) => {
        const { step, questions, answers } = this.state;
        const { ObjectId, ObjectType, surveyToShow } = this.props;

        if (questions[step].key !== "gia_cert_number") {
            return false;
        }

        const certificateNumber = answers.gia_cert_number;
        const increment = certificateNumber ? 4 : 1;
        const buttonClicked = certificateNumber
            ? "Next"
            : "I don’t have the report number at hand";

        UserActions.survey(
            `${buttonClicked} button clicked`,
            ObjectId,
            ObjectType,
            "",
            surveyToShow
        );

        if (certificateNumber) {
            WorthyApi.updateCertificate(ObjectId, certificateNumber).then(
                () => {
                    if (stopAnimation) stopAnimation();
                    this.setState({ step: step + increment });
                    const { ObjectId, updateItem } = this.props;
                    updateItem(ObjectId);
                }
            );
        } else {
            if (stopAnimation) stopAnimation();
            this.setState({ step: step + increment });
        }

        return true;
    };

    handledCertificateStandalone = (stopAnimation) => {
        const { step, questions, answers } = this.state;
        const { ObjectId, defaultAnswers = {} } = this.props;

        if (questions[step].key !== "gia_cert_number_standalone") {
            return false;
        }
        const certificateNumber = answers.gia_cert_number_standalone;
        const certificateChanged =
            defaultAnswers.gia_cert_number_standalone !== certificateNumber;
        if (certificateNumber && certificateChanged) {
            WorthyApi.updateCertificate(ObjectId, certificateNumber)
                .then(async (res) => {
                    this.props.onClose(res);
                })
                .catch(() => {
                    this.props.onClose(
                        new Error("Failed to update certificate")
                    );
                })
                .finally(() => {
                    if (stopAnimation) {
                        stopAnimation();
                    }
                });
        } else {
            this.props.onClose();
        }

        return true;
    };

    setSurveyTitle(newStep) {
        const { surveyToShow } = this.props;
        const titleText = I18n.getString(`survey::${surveyToShow}::title`);

        // Show survey title only on the first step
        if (newStep === 0) {
            this.props.setTitleDesktop(titleText);
            // this.props.setTitleMobile("");
        } else {
            this.props.setTitleDesktop("");
            // this.props.setTitleMobile("");
        }
    }

    handleQuestion = (question) => {
        const { answers } = this.state;

        if (question.questions) {
            return question.questions.forEach((q) => this.handleQuestion(q));
        }
        const answerDefaultValue = question.type === "multipleChoice" ? [] : "";
        const answer = answers[question.key] || answerDefaultValue;

        GA.postRegistrationSurveyNext({
            question: question.eventText,
            answer:
                (question.type === "freeText" && answer) || _.camelCase(answer),
        });

        if (question.q.includes("color") || question.q.includes("clarity")) {
            this.updateColorOrClarity(answer, question.q);
        }

        if (question.key === "center_diamond_carat_weight") {
            this.updateCaratWeight(answer);
        }

        this.saveEvent(
            question.q,
            typeof answer === "string" ? [answer] : answer
        );
    };

    increaseStep(stopAnimation) {
        let { step, questions, answers, hideThankYouStep } = this.state;
        const {
            ObjectId,
            ObjectType,
            surveyToShow,
            showThankYouExp,
            setTitleMobile,
            setTitleDesktop,
        } = this.props;

        const singleAnswer = answers[questions[step].key];

        if (this.handledCertificateStep(stopAnimation)) return;
        if (this.handledCertificateStandalone(stopAnimation)) return;

        const buttonText =
            step === questions.length
                ? "Let's Continue"
                : step === questions.length - 1
                ? "Finish"
                : "Next";

        UserActions.survey(
            `${buttonText} button clicked`,
            ObjectId,
            ObjectType,
            "",
            surveyToShow
        );
        if (step < questions.length) {
            this.handleQuestion(questions[step]);

            this.setSurveyTitle(step + 1);

            if (step === questions.length - 1) {
                if (
                    hideThankYouStep ||
                    (showThankYouExp &&
                        singleAnswer &&
                        ["Next 30 days", "Next 3 months"].includes(
                            singleAnswer
                        ))
                ) {
                    return this.props.onClose();
                } else {
                    let titleToSet = showThankYouExp
                        ? "Thanks!"
                        : surveyToShow === "listingDraftNoPhone"
                        ? "Thank You!"
                        : "All Set!";
                    this.isMobile
                        ? setTitleMobile(titleToSet)
                        : setTitleDesktop(titleToSet);
                }
            }

            this.setState({
                step: step + 1,
            });
        } else {
            this.props.onClose();
        }

        if (stopAnimation) {
            stopAnimation();
        }
    }

    decreaseStep() {
        let { step } = this.state;
        const { ObjectId, ObjectType, surveyToShow } = this.props;

        UserActions.survey(
            "Back button clicked",
            ObjectId,
            ObjectType,
            "",
            surveyToShow
        );
        this.setSurveyTitle(step - 1);

        if (step !== 0) {
            this.setState({
                step: step - 1,
            });
        }
    }

    renderSingleChoice(question) {
        const { answers } = this.state;
        const singleAnswer = answers[question.key];

        return (
            <CheckboxGroup
                question={question}
                selected={singleAnswer}
                isMobile={this.isMobile}
                containerClassName={"single-choice-container"}
                showOther={singleAnswer?.includes("other")}
                onAfterOtherChange={(v) => this.onChangeSingle(question.key, v)}
                onChange={(e) => {
                    this.onChangeSingle(question.key, e.target.name);
                }}
            />
        );
    }

    renderMultipleChoice(question) {
        const { answers } = this.state;
        const multipleAnswers = answers[question.key] || [];

        return (
            <CheckboxGroup
                question={question}
                selected={multipleAnswers}
                isMobile={this.isMobile}
                containerClassName={"multiple-choice-container"}
                showOther={multipleAnswers.includes("other")}
                onChange={(e) => {
                    this.onChangeMultiple(
                        question.key,
                        e.target.name,
                        e.target.checked
                    );
                }}
                onAfterOtherChange={(value) => {
                    this.onChangeMultiple(question.key, value, true);
                }}
            />
        );
    }

    renderFreeText(question) {
        const { answers } = this.state;
        const singleAnswer = answers[question.key];

        return (
            <FreeText
                question={question}
                onChange={(e) =>
                    this.onChangeSingle(question.key, e.target.value)
                }
                value={singleAnswer || ""}
                {...question.inputProps}
            />
        );
    }

    renderWeight(question) {
        const { answers } = this.state;
        const singleAnswer = answers[question.key];

        return (
            <Weight
                question={question}
                onChange={(value) => this.onChangeSingle(question.key, value)}
                value={singleAnswer || ""}
            />
        );
    }

    renderChocolateTableChoice(question) {
        const { answers } = this.state;
        const singleAnswer = answers[question.key];

        return (
            <ChocolateTableChoice
                question={question}
                onChange={(v) => this.onChangeSingle(question.key, v)}
                selectedValue={singleAnswer}
            />
        );
    }

    renderButtonsStep(question) {
        return (
            <Buttons
                onChange={this[question.action]}
                buttons={question.buttons}
                text={question.q}
            />
        );
    }

    renderTextStep(question) {
        const { answers } = this.state;
        const singleAnswer = answers[question.key];

        return (
            <Text
                placeholder={question.placeholder}
                question={question}
                onChange={(e) =>
                    this.onChangeSingle(question.key, e.target.value)
                }
                value={singleAnswer || ""}
                {...question.inputProps}
            />
        );
    }

    renderQuestion(questionToRender) {
        if (!questionToRender) {
            return;
        }

        if (questionToRender.questions) {
            return (
                <MultiInputWrapper>
                    {questionToRender.questions.map((question) =>
                        this.renderQuestion(question)
                    )}
                </MultiInputWrapper>
            );
        }

        switch (questionToRender.type) {
            case "singleChoice":
                return this.renderSingleChoice(questionToRender);
            case "multipleChoice":
                return this.renderMultipleChoice(questionToRender);
            case "chocolateTableChoice":
                return this.renderChocolateTableChoice(questionToRender);
            case "freeText":
                return this.renderFreeText(questionToRender);
            case "weight":
                return this.renderWeight(questionToRender);
            case "buttons":
                return this.renderButtonsStep(questionToRender);
            case "text":
                return this.renderTextStep(questionToRender);
        }
    }

    renderThankYouStep() {
        const { surveyToShow, showThankYouExp } = this.props;

        if (showThankYouExp) {
            return (
                <div className={"thank-you-step-container"}>
                    <div className={"subtitle-second"}>
                        {I18n.getString(
                            `survey::${surveyToShow}::thankYouSubTitle`
                        )}
                    </div>
                    <div className={"thank-you-footer"}>
                        <div className={"title"}>
                            {I18n.getString(
                                `survey::${surveyToShow}::thankYouFooterTitle`
                            )}
                        </div>
                        <div
                            className={"subtitle"}
                            onClick={() => {
                                this.saveEvent("Learning center link clicked");
                            }}
                            dangerouslySetInnerHTML={{
                                __html: I18n.getString(
                                    `survey::${surveyToShow}::thankYouFooterSubTitle`
                                ).replace(/{app_url}/g, appUrl()),
                            }}
                        ></div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={"thank-you-step-container"}>
                    <div className={"image"} />
                </div>
            );
        }
    }

    renderSkipCertificateButton = () => {
        const { surveyToShow } = this.props;
        const { step, questions } = this.state;
        const isCertNumber = questions[step].key === "gia_cert_number";

        if (surveyToShow !== "giaCertificateOrProperties") {
            return null;
        }

        if (!isCertNumber) {
            return null;
        }

        return (
            <MediumLinkButton
                text="I don’t have the report number on hand"
                click={() => this.increaseStep()}
                overrideWrapperCSS={{ marginTop: 12, marginBottom: -36 }}
            />
        );
    };

    renderButtons() {
        const { step, questions } = this.state;
        const { surveyToShow, showThankYouExp } = this.props;
        const firstStep = step === 0;

        if (showThankYouExp && step === questions.length) return;
        let buttonText =
            step === questions.length
                ? "Let's Continue"
                : step === questions.length - 1
                ? "Submit"
                : "Next";
        if (surveyToShow === "listingDraftNoPhone") {
            buttonText = "Submit";
            if (step === questions.length) return null;
        }

        return (
            <div
                className={`buttons-container center ${
                    this.isMobile ? "mobile" : "desktop"
                } ${firstStep ? "" : "short"}`}
            >
                {firstStep || step === questions.length ? null : (
                    <div style={{ display: "none" }} className={"back-link"}>
                        <MediumLinkButton
                            text={"Back"}
                            click={() => this.decreaseStep()}
                        />
                    </div>
                )}
                {questions[step].type !== "buttons" && (
                    <>
                        <MediumPrimaryButton
                            text={buttonText}
                            click={(stopAnimation) =>
                                this.increaseStep(stopAnimation)
                            }
                            overrideCSS={{ right: 0, display: "block" }}
                        />
                    </>
                )}
                <div className="progress-bar-container">
                    {questions.length > 1 &&
                        questions.map((v, i) => (
                            <div
                                className={`step-dot ${
                                    i === step ? "active" : ""
                                }`}
                                key={v.id}
                                id={v.id}
                                onClick={(event) =>
                                    this.progressBarClicked(event)
                                }
                            />
                        ))}
                </div>
            </div>
        );
    }

    render() {
        const { step, questions } = this.state;
        const { className, surveyToShow } = this.props;
        const question = questions[step];
        const subTitleText = I18n.getString(
            `survey::${surveyToShow}::subTitle`
        );

        return (
            <>
                {question.title && (
                    <Title withBorder={!this.isMobile}>{question.title}</Title>
                )}
                <div
                    key="survey-container"
                    className={`${className} survey-container-wrapper survey-container`}
                >
                    {step === 0 ? (
                        <>
                            {step < questions.length &&
                            subTitleText &&
                            !question.title ? (
                                <span className={"subtitle"}>
                                    {subTitleText}
                                </span>
                            ) : null}
                            {!question.title && (
                                <span
                                    className={`survey-vector ${
                                        this.isMobile ? "mobile" : ""
                                    }`}
                                />
                            )}
                        </>
                    ) : null}
                    {step < questions.length
                        ? this.renderQuestion(question)
                        : this.renderThankYouStep()}
                    {this.renderButtons()}
                </div>
            </>
        );
    }
}

Survey.propTypes = {
    surveyToShow: PropTypes.string,
    showThankYouExp: PropTypes.bool,
    className: PropTypes.string,
    ObjectId: PropTypes.any,
    ObjectType: PropTypes.any,
    onClose: PropTypes.func,
    setTitleMobile: PropTypes.func,
    setTitleDesktop: PropTypes.func,
    updateItem: PropTypes.func,
    isGemstone: PropTypes.bool,
    defaultAnswers: PropTypes.shape(),
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({ updateItem }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(Survey);
